import { useState } from "react";
import * as Icons from "lucide-react";

const CONF_TONE = {
  High: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-100 text-slate-500 border-slate-200",
};

// Staff-only Knowledge Retrieval Summary / Research Package — collapsible. Never client/Penny-facing.
// Shared by Case Analysis and Organizer Review (identical design, read-only).
export function KnowledgeRetrievalPanel({ research, summary, testidPrefix = "ca" }) {
  const [open, setOpen] = useState(false);
  if (!research && !summary) return null;
  const r = research || {};
  const s = summary || {};
  const conf = r.confidence || s.match_confidence || "Medium";
  const authorities = r.authorities || [];
  const related = r.related_authorities || [];
  const gaps = r.knowledge_gaps || [];
  const topics = r.topics || s.topics || [];
  const jurisdictions = r.jurisdictions || (s.jurisdiction ? [s.jurisdiction] : []);
  return (
    <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden" data-testid={`${testidPrefix}-knowledge-retrieval`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testidPrefix}-knowledge-toggle`}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-indigo-50 transition-colors">
        <Icons.Sparkles className="h-4 w-4 text-indigo-500 shrink-0" />
        <span className="text-sm font-semibold text-indigo-800 flex-1">Knowledge Retrieval Summary
          <span className="ml-2 text-[11px] font-normal text-indigo-500">· {authorities.length} authorities · staff-only</span>
        </span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${CONF_TONE[conf] || CONF_TONE.Medium}`}>{conf} confidence</span>
        <Icons.ChevronDown className={`h-4 w-4 text-indigo-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-[13px]" data-testid={`${testidPrefix}-knowledge-body`}>
          {jurisdictions.length > 0 && <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Jurisdictions detected</div><div className="flex flex-wrap gap-1.5">{jurisdictions.map((j, i) => <span key={i} className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">{j}</span>)}</div></div>}
          {topics.length > 0 && <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Topics extracted</div><div className="flex flex-wrap gap-1.5">{topics.slice(0, 10).map((t, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{t}</span>)}</div></div>}
          <div className="md:col-span-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Authorities consulted</div>
            {authorities.length === 0 ? <div className="text-slate-400 text-xs">None matched.</div> :
              <ul className="space-y-1">{authorities.map((a, i) => (
                <li key={i} className="flex items-center gap-2"><Icons.FileText className="h-3.5 w-3.5 text-indigo-400 shrink-0" /><span className="text-slate-700 truncate">{a.title}</span>
                  {a.authority_level && <span className="text-[10px] bg-indigo-50 text-indigo-600 rounded px-1 shrink-0">{a.authority_level}</span>}
                  {(a.matched_on || []).length > 0 && <span className="text-[10px] text-slate-400 shrink-0">matched: {a.matched_on.join(", ")}</span>}
                </li>))}</ul>}
          </div>
          {related.length > 0 && <div className="md:col-span-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Related authorities</div>
            <ul className="space-y-0.5">{related.map((rl, i) => <li key={i} className="text-slate-500 text-xs">• {rl.title} <span className="text-slate-400">({(rl.relationship_type || "").replace(/_/g, " ")})</span></li>)}</ul></div>}
          {gaps.length > 0 && <div className="md:col-span-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-amber-500 mb-1">Knowledge gaps</div>
            <ul className="space-y-0.5">{gaps.map((g, i) => <li key={i} className="text-amber-700 text-xs flex items-start gap-1"><Icons.AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{g}</li>)}</ul></div>}
          <div className="md:col-span-2 flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-indigo-100">
            <span>Retrieval: {s.retrieval_method || r.metrics?.retrieval_method || "—"}{r.metrics?.retrieval_ms != null ? ` · ${Math.round(r.metrics.retrieval_ms)}ms` : ""}</span>
            <span className="italic">The source documents remain the legal authority.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Staff-only Historical Context (Case Context Package) — collapsible. Never client/Penny-facing.
// `onResolveContradiction(contradiction)` (optional) surfaces the governed Resolve action when a
// contradiction maps to a governed client-record field.
export function HistoricalContextPanel({ context, testidPrefix = "ca" }) {
  const [open, setOpen] = useState(false);
  if (!context || (context.metrics?.records_retrieved || 0) === 0) return null;
  const c = context;
  const facts = c.relevant_historical_facts || [];
  const decisions = c.prior_decisions || [];
  const outstanding = c.outstanding_questions || [];
  const contradictions = c.contradictions || [];
  return (
    <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50/40 overflow-hidden" data-testid={`${testidPrefix}-historical-context`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testidPrefix}-history-toggle`}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-50 transition-colors">
        <Icons.History className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="text-sm font-semibold text-amber-800 flex-1">Historical Context
          <span className="ml-2 text-[11px] font-normal text-amber-600">· {c.metrics.records_retrieved} records · staff-only</span>
        </span>
        {contradictions.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-medium">{contradictions.length} contradiction{contradictions.length > 1 ? "s" : ""}</span>}
        <Icons.ChevronDown className={`h-4 w-4 text-amber-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 text-[13px]" data-testid={`${testidPrefix}-history-body`}>
          {contradictions.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-rose-500 mb-1">Contradictions (staff to resolve — not auto-resolved)</div>
              <ul className="space-y-1">{contradictions.map((ct, i) => (
                <li key={i} className="text-xs text-rose-700 bg-rose-50 rounded px-2 py-1"><span className="font-semibold">{ct.field}:</span> {ct.values.map((v) => `${v.value} (${v.reliability_label})`).join("  vs  ")}</li>
              ))}</ul></div>
          )}
          {facts.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Relevant historical facts</div>
              <ul className="space-y-1">{facts.slice(0, 12).map((ftc, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <Icons.Dot className="h-4 w-4 text-amber-400 shrink-0" />
                  <span className="flex-1">{ftc.fact}
                    <span className="ml-1 text-[10px] bg-slate-100 text-slate-500 rounded px-1">{ftc.reliability_label}</span>
                    {ftc.support_count > 1 && <span className="ml-1 text-[10px] text-emerald-600">{ftc.support_count} sources</span>}
                    {ftc.superseded && <span className="ml-1 text-[10px] bg-slate-200 text-slate-500 rounded px-1">superseded</span>}
                  </span>
                </li>))}</ul></div>
          )}
          {decisions.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Prior decisions</div>
              <ul className="space-y-0.5">{decisions.slice(0, 8).map((d, i) => <li key={i} className="text-xs text-slate-500">• {d.decision} {d.approved && <span className="text-emerald-600">(approved)</span>}</li>)}</ul></div>
          )}
          {outstanding.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Outstanding questions</div>
              <ul className="space-y-0.5">{outstanding.slice(0, 8).map((q, i) => <li key={i} className="text-xs text-slate-500">• {q.question}</li>)}</ul></div>
          )}
          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-amber-100">
            <span>Retrieval: {c.metrics.retrieval_ms != null ? `${Math.round(c.metrics.retrieval_ms)}ms` : "—"} · {c.metrics.records_retrieved} records · {c.metrics.contradictions_identified} contradictions</span>
            <span className="italic">Historical assertions — prior AI output is not self-validating.</span>
          </div>
        </div>
      )}
    </div>
  );
}
